<script>
	import { appProfileState } from "../../state/ApplicationState.svelte";
	import PersonalInfo from "./PersonalInfo.svelte";
	import Dependents from "./Dependents.svelte";
	import BankAccounts from "./BankAccounts.svelte";
	import SecuritySettings from "./SecuritySettings.svelte";

	const COMPONENTS = {
		PersonalInfo: {
			id: "PersonalInfo",
			key: "", // default/no query param
			title: "Personal Info",
		},
		Dependents: {
			id: "Dependents",
			key: "d",
			title: "Dependents",
			forSubscriberOnly: true,
		},
		FinancialInstitutions: {
			id: "FinancialInstitutions",
			key: "fi",
			title: "Financial Institutions",
			forSubscriberOnly: true,
		},
		Security: {
			id: "Security",
			key: "s",
			title: "Security Settings",
		},
	};

	// Key:Value Map of components by id
	const COMPONENT_BY_ID = new Map(
		Object.values(COMPONENTS).map((c) => [c.id, c]),
	);

	// Key:Value Map of components by key
	const COMPONENT_BY_KEY = new Map(
		Object.values(COMPONENTS).map((c) => [String(c.key).toLowerCase(), c]),
	);

	let selectedComponentId = $state(COMPONENTS.PersonalInfo.id);

	let selectedComponent = $derived.by(() => {
		return (
			COMPONENT_BY_ID.get(selectedComponentId) || COMPONENTS.PersonalInfo
		);
	});

	let sideMenuItems = $derived.by(() => {
		const isSubscriber = appProfileState.isUserSubscriber === true;
		return Object.values(COMPONENTS)
			.filter((c) => !c.forSubscriberOnly || isSubscriber)
			.map((c) => ({ id: c.id, title: c.title, key: c.key }));
	});

	// Given a URL 'component' key, resolve to a safe component
	function resolveSelectedComponent(key) {
		const isSubscriber = appProfileState.isUserSubscriber === true;
		const candidate =
			(key && COMPONENT_BY_KEY.get(String(key).toLowerCase())) ||
			undefined;

		if (!candidate) return COMPONENTS.PersonalInfo;

		const allowed = !candidate.forSubscriberOnly || isSubscriber;
		return allowed ? candidate : COMPONENTS.PersonalInfo;
	}

	function getCurrentUrl() {
		return new URL(window.location.href);
	}
	
	// Push/replace history + document title
	function updateHistory(url, title, replace = false) {
		document.title = title;
		const state = { title };
		if (replace) {
			window.history.replaceState(state, "", url);
		} else {
			window.history.pushState(state, "", url);
		}
	}

	// Read URL and set selection (used for deep link + popstate)
	function applySelectionFromUrl({ replace = false } = {}) {
		const key = getCurrentUrl().searchParams.get("component");
		const next = resolveSelectedComponent(key);

		selectedComponentId = next.id;

		const url = getCurrentUrl();
		const isDefault = !key || next.key === "";
		if (isDefault) {
			url.searchParams.delete("component");
			updateHistory(url, `Profile: ${next.title}`, replace);
		} else {
			document.title = `Profile: ${next.title}`;
		}
	}

	function menuClicked(item) {
		const next = resolveSelectedComponent(item.key);

		const url = getCurrentUrl();
		if (next.key) {
			url.searchParams.set("component", next.key);
		} else {
			url.searchParams.delete("component");
		}

		selectedComponentId = next.id;
		updateHistory(url, `Profile: ${next.title}`, false);
	}

	// ---- Runes effects ----
	// Initialize from URL once after mount
	let _initialized = $state(false);
	$effect(() => {
		if (typeof window === "undefined" || _initialized) return;
		_initialized = true;
		applySelectionFromUrl({ replace: true });
	});

	// Listen to back/forward navigation
	$effect(() => {
		if (typeof window === "undefined") return;

		const onPopState = (event) => {
			if (event.state && event.state.title) {
				document.title = event.state.title;
			}
			applySelectionFromUrl();
		};

		window.addEventListener("popstate", onPopState);
		return () => window.removeEventListener("popstate", onPopState);
	});
</script>

<!-- <pre>{JSON.stringify(selectedComponent)}</pre> -->
<main class="container-fluid">
	<div class="row">
		<div class="col col-sm-3 col-md-3 col-lg-2 ms-0 me-0 ps-0 pe-0 menu">
			<h4 class="mt-4 mb-3 ms-3">Profile &amp; Settings</h4>
			{#each sideMenuItems as item}
				<div>
					<button
						type="button"
						class="profile-button"
						class:active={item.id === selectedComponent.id}
						onclick={() => menuClicked(item)}>{item.title}</button
					>
				</div>
			{/each}
		</div>

		<div class="col col-sm-9 col-md-9 col-lg-10 content-pane ps-3">
			{#if selectedComponent.id === COMPONENTS.PersonalInfo.id}
				<PersonalInfo />
			{:else if selectedComponent.id === COMPONENTS.Dependents.id}
				<Dependents />
			{:else if selectedComponent.id === COMPONENTS.FinancialInstitutions.id}
				<BankAccounts />
			{:else if selectedComponent.id === COMPONENTS.Security.id}
				<SecuritySettings />
			{/if}
		</div>
	</div>
</main>
